import PropTypes from "prop-types";
import { useCallback } from "react";
import { default as lodashPick } from "lodash-es/pick";
import fromPairs from "lodash-es/fromPairs";
import { connect } from "react-redux";
import moment from "moment";
import "moment/locale/it";
import "moment/locale/es-us";

import { getPrice } from "./price";
import {
  setName,
  setSurname,
  setCountry,
  setPostcode,
  setEmail,
  setPhoneNumber,
  setNumRoomGuests,
  setRoomKind,
  setRoomDates,
  setSelectedExperiences,
  setNumSelectedExperiencesGuests,
  setExperiencesDates,
  setExperiencesTimes,
  setNotes,
  setHasExperience,
  setHasRoomKind,
  setStatus,
  setHasTable
} from "../store/actions";

export const formatPrice = value => {
  const before = Math.floor(value);
  const rest = value - before;
  const firstRestDigit = Math.floor(rest * 10);
  const secondRestDigit = Math.floor((rest * 10 - firstRestDigit) * 10);
  return `${before},${firstRestDigit}${secondRestDigit}`;
};

export const webAbsoluteUrl =
  window._spPageContextInfo.webAbsoluteUrl.indexOf("localhost") >= 0
    ? `https://tenutedelcerro-test.servizi.gr-u.it${window._spPageContextInfo.webServerRelativeUrl}`
    : window._spPageContextInfo.webAbsoluteUrl;

export const cleanPrice = price => {
  if (price) {
    return parseInt(price.replace("€", "").trimEnd());
  }
  return null;
};

String.prototype.format = function(args) {
  var str = this;
  return str.replace(String.prototype.format.regex, function(item) {
    var intVal = parseInt(item.substring(1, item.length - 1));
    var replace;
    if (intVal >= 0) {
      replace = args[intVal];
    } else if (intVal === -1) {
      replace = "{";
    } else if (intVal === -2) {
      replace = "}";
    } else {
      replace = "";
    }
    return replace;
  });
};
String.prototype.format.regex = new RegExp("{-?[0-9]+}", "g");

export const getSlots = date =>
  date ? date.split(":").map(value => parseInt(value)) : null;

export const getWines = items =>
  (items instanceof Array
    ? items.map(item => item.Label)
    : items.split(";")
  ).filter(x => x);

export const getNumDays = date => {
  if (date instanceof Array) {
    const diffTime = Math.abs(date[1].getTime() - date[0].getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } else {
    return 1;
  }
};

export const DateType = PropTypes.oneOfType([
  PropTypes.instanceOf(Date),
  PropTypes.arrayOf(PropTypes.instanceOf(Date))
]);

export const IdType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

export const experiencePropType = PropTypes.shape({
  id: IdType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  duration: PropTypes.string.isRequired,
  glasses: PropTypes.string,
  price: PropTypes.number.isRequired,
  wines: PropTypes.arrayOf(PropTypes.string.isRequired),
  morningSlotStart: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  morningSlotEnd: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  afternoonSlotStart: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  afternoonSlotEnd: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired
});

export const experiencesPricesPropType = PropTypes.objectOf(
  PropTypes.number.isRequired
);

export const experiencesDatesPropType = PropTypes.objectOf(DateType);

export const TimeType = PropTypes.arrayOf(PropTypes.number);

export const experiencesTimesPropType = PropTypes.objectOf(TimeType);

export const GuestsType = PropTypes.arrayOf(PropTypes.number);
export const numSelectedExperiencesGuestsPropType = PropTypes.objectOf(
  GuestsType
);

export const useCombinedCallbacks = (...callbacks) =>
  useCallback(
    newValue =>
      callbacks.forEach(callback => {
        if (callback) {
          callback(newValue);
        }
      }),
    [callbacks]
  );

export const formatDate = date =>
  `${date.getDate()} / ${date.getMonth() + 1} / ${date.getFullYear()}`;

export async function computePrice(
  experience,
  numSelectedExperiencesGuests,
  roomKind,
  numRoomGuests,
  numDays
) {
  return (
    (experience ? experience.price * numSelectedExperiencesGuests : 0) +
    (roomKind ? getPrice(roomKind, numRoomGuests, numDays) : 0)
  );
}

export const getFormattedTime = time =>
  typeof time == "string"
    ? time
    : [time[0], time[1] < 10 ? "0" + time[1] : time[1]].join(":");

export const getFormattedDateTime = (date, time) =>
  (date instanceof Array
    ? formatDate(date[0]) + " - " + formatDate(date[1])
    : formatDate(date)) + (time ? " alle ore " + getFormattedTime(time) : "");

export const pick = propNames => (state, ownProps) => ({
  ...ownProps,
  ...lodashPick(state, propNames)
});

const propsToSetter = {
  name: setName,
  surname: setSurname,
  country: setCountry,
  postcode: setPostcode,
  email: setEmail,
  phoneNumber: setPhoneNumber,
  numRoomGuests: setNumRoomGuests,
  roomKind: setRoomKind,
  roomDates: setRoomDates,
  selectedExperiences: setSelectedExperiences,
  numSelectedExperiencesGuests: setNumSelectedExperiencesGuests,
  experiencesDates: setExperiencesDates,
  experiencesTimes: setExperiencesTimes,
  notes: setNotes,
  hasExperience: setHasExperience,
  hasRoomKind: setHasRoomKind,
  status: setStatus,
  hasTable: setHasTable
};

export const connectToProps = (
  propsList,
  extraMapStateToProps = () => {},
  extraMapDispatchToProps = () => {}
) =>
  connect(
    state => ({ ...pick(propsList)(state), ...extraMapStateToProps(state) }),
    dispatch => ({
      ...fromPairs(
        propsList.map(propName => [
          "set" + propName[0].toUpperCase() + propName.slice(1),
          value => {
            if (!propsToSetter[propName]) {
              console.error(`Cannot find ${propName} in the propsToSetter map`);
            }
            dispatch(propsToSetter[propName](value));
          }
        ])
      ),
      ...extraMapDispatchToProps(dispatch)
    })
  );

export const getEstateName = () =>
  document.getElementById("tenuta").innerHTML.trim();

export const lang = window._spPageContextInfo.webServerRelativeUrl.slice(1);
const t = window.TdC.translations;
const decapitalize = s => s && s[0].toLowerCase() + s.slice(1);
export const _ = fromPairs(
  Object.getOwnPropertyNames(t)
    .filter(name => name.indexOf("reservation") === 0)
    .map(name => [
      decapitalize(name.slice("reservation".length)),
      t[name][lang]
    ])
);
console.log(_);

export const Moment = Object.getPrototypeOf(moment()).constructor;
